import React from 'react';

import CardDialog from '~/components/CardDialog';
import { FsButton } from '~/components/core';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Text from '~/components/core/TextComponents/Text';

import { DIALOG_FOOTER_CLASS_NAME, HANDLER_NOTE_MAX_LENGTH, HANDLER_NOTE_MAX_ROWS } from './constants';
import { handlerNoteCounter } from './utils';

export interface DismissWithReasonDialogProps {
  /** The item's name, as its card titles it, so the handler sees what they are turning down. */
  title: string;
  /** The exposure, where there is one: the card names it under its title, so the dialog does too. */
  subtitle?: string;
  /** What is being turned down. Opens the body sentence, and decides what it says the note is for. */
  subject: 'recommendation' | 'request';
  /** Primary button label, e.g. "Dismiss". */
  confirmLabel: string;
  /** Resolves once the item is turned down. Closing the dialog is the caller's to do, not ours. */
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}

/**
 * What Clive does with the note. A rejected recommendation is not proposed again without new
 * evidence; a rejected request is worked around instead, so its note is the handler's chance to tell
 * Clive what they know.
 */
const CONSEQUENCE: Record<DismissWithReasonDialogProps['subject'], string> = {
  recommendation: 'Clive reads this on its next run and will not re-propose without new evidence.',
  request: 'Clive works around it rather than asking again — tell it what you know, not just that you are declining.',
};

/**
 * Turning a Clive item down, with the handler's reason.
 *
 * One component for both gestures — rejecting a recommendation and rejecting a request — because
 * they are the same act: declining something Clive raised, with a note it reads on its next run.
 * Only the noun and what Clive does with the note differ. The note is required, so the confirm
 * button stays disabled until one is written.
 */
export const DismissWithReasonDialog: React.FC<DismissWithReasonDialogProps> = ({
  title,
  subtitle,
  subject,
  confirmLabel,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const trimmedReason = reason.trim();

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(trimmedReason);
      // Deliberately no reset on success: `onConfirm` closes the dialog, so the buttons stay
      // disabled for the rest of this dialog's life rather than re-enabling behind the close.
    } catch {
      // `useActionCard.dismissAction`, or `useRejectRequest` for a request, already ran the failure
      // through `reportAxiosError` before rethrowing, so there is nothing left to report here.
      // Swallowing it leaves the dialog open on its own state, with the note the handler wrote
      // still in the field to retry with.
      setIsSubmitting(false);
    }
  };

  const footerActions = (
    <div className="flex w-full justify-end gap-12">
      <FsButton variant="text" className="text-blue-600" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </FsButton>
      <FsButton variant="contained" color="primary" onClick={handleConfirm} disabled={isSubmitting || !trimmedReason}>
        {confirmLabel}
      </FsButton>
    </div>
  );

  return (
    <CardDialog
      isDialog
      title={title}
      subheader={
        subtitle ? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {subtitle}
          </Text>
        ) : undefined
      }
      onClose={onClose}
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
      footerClassName={DIALOG_FOOTER_CLASS_NAME}
      footerActions={footerActions}
    >
      <div className="flex flex-col gap-16 pb-8 pt-4">
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {`Tell Clive why you are turning this ${subject} down. ${CONSEQUENCE[subject]}`}
        </Text>
        <TextField
          label="Reason"
          size="medium"
          value={reason}
          onChange={setReason}
          multiline
          minRows={3}
          maxRows={HANDLER_NOTE_MAX_ROWS}
          fullWidth
          autoFocus
          disabled={isSubmitting}
          inputProps={{ maxLength: HANDLER_NOTE_MAX_LENGTH }}
          helperText={handlerNoteCounter(reason)}
        />
      </div>
    </CardDialog>
  );
};
